import Brightness4Icon from "@mui/icons-material/Brightness4";
import Brightness7Icon from "@mui/icons-material/Brightness7";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import {
  Avatar,
  Box,
  Container,
  Grid,
  IconButton,
  Paper,
  Typography,
} from "@mui/material";
import { isEmpty } from "lodash";
import { extendMoment } from "moment-range";
import Moment from "moment-timezone";
import { useContext, useEffect, useState } from "react";
import { StyleSheet } from "react-native";
import { Calendar } from "react-native-calendars";
import { useParams, useSearchParams } from "react-router-dom";
import AgendaList from "./AgendaList";
import AuthContext from "./AuthContext";
import LoadingScreen from "./LoadingScreen";
import { randomColor } from "./utils";
const axios = require("axios").default;
const moment = extendMoment(Moment);

// Clean up malformed link parameters (e.g., from broken markdown links)
// Handles cases like "agency-demo](https://..." -> "agency-demo"
const sanitizeLink = (link) => {
  if (!link) return link;
  // Remove any trailing markdown link syntax that got URL-encoded
  const bracketIndex = link.indexOf("]");
  if (bracketIndex > 0) {
    return link.substring(0, bracketIndex);
  }
  return link;
};

const ScheduleScreen = (props) => {
  const {
    theme,
    setThemeMode,
    mode,
    updateThemeColors,
    secondaryColor,
    primaryColor,
    setErrorMessage,
  } = useContext(AuthContext);
  const [user, setUser] = useState({ name: null });
  const [times, setTimes] = useState({
    original: [],
    filtered: [],
    marked: {},
  });
  const [event, setEvent] = useState({ meetingFutureBooking: 60 });
  const [settings, setSettings] = useState({});
  const [localSettings, setLocalSettings] = useState({
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  });
  const [loadingScreen, setLoadingScreen] = useState(true);
  const [spinner, setSpinner] = useState(false);

  const [dates, setDates] = useState({
    selectedDate: null,
    todayDate: moment(),
    startDate: moment().startOf("month").isAfter(moment())
      ? moment().startOf("month")
      : moment(),
    selectedMonth: moment(),
    startOfMonth: moment().startOf("month"),
    endOfMonth: moment().endOf("month"),
    monthRange: moment.range(
      moment().startOf("month"),
      moment().endOf("month")
    ),
    maxStartDate: moment().add(event.meetingFutureBooking, "days"),
  });
  const { username, link: rawLink } = useParams();
  const link = sanitizeLink(rawLink);
  const [searchParams] = useSearchParams();

  // Capture tracking code from URL and persist to localStorage
  useEffect(() => {
    const code = searchParams.get("code");
    if (code) {
      localStorage.setItem("mq_tracking_code", code);
    }
  }, [searchParams]);

  const getAvailableTimes = () => {
    setSpinner(true);
    const sd = dates.startDate.format("YYYY-MM-DD");
    const ed = dates.endOfMonth.format("YYYY-MM-DD");
    if (dates.startDate.isAfter(dates.maxStartDate)) {
      setSpinner(false);
      setErrorMessage(
        `Meetings can only be scheduled ${event.meetingFutureBooking} days in the future`,
        3000
      );
      return;
    }
    console.log("Fetching available times", username, sd, ed);
    axios
      .get(
        "https://hlpscbffug.execute-api.us-west-2.amazonaws.com/prod/available/times",
        {
          params: {
            username: username,
            link: link,
            startDate: sd,
            endDate: ed,
            offset: moment().utcOffset(),
          },
        }
      )
      .then((res) => {
        if (res.data !== "") {
          const primaryColor = res.data.event.color || randomColor();
          const userObj = formatUser(res.data.user, res.data.settings);
          const sections = getSections(
            res.data.schedule,
            userObj,
            res.data.event
          );
          const selectedDateStr = isEmpty(dates.selectedDate)
            ? getFirstDate(sections)
            : dates.selectedDate;
          setUser(userObj);
          setEvent({ ...event, ...res.data.event });
          setSettings(res.data.settings);

          // If first available date is in a different month, move calendar forward
          const firstAvailableMoment = moment(selectedDateStr);
          if (firstAvailableMoment.isAfter(dates.endOfMonth)) {
            const newMonth = firstAvailableMoment.clone().startOf("month");
            setDates({
              ...dates,
              selectedDate: selectedDateStr,
              todayDate: moment(newMonth),
              selectedMonth: moment(newMonth),
              startDate: newMonth.isAfter(moment()) ? moment(newMonth) : moment(),
              startOfMonth: moment(newMonth).startOf("month"),
              endOfMonth: moment(newMonth).endOf("month"),
              monthRange: moment.range(
                moment(newMonth).startOf("month"),
                moment(newMonth).endOf("month")
              ),
            });
          }

          setTimesData(sections, selectedDateStr, primaryColor);
          updateThemeColors(mode, primaryColor, secondaryColor);
          setLoadingScreen(false);
          setSpinner(false);
        }
      });
  };

  const setTimesData = (sections, selectedDateStr, primaryColor) => {
    setTimes({
      original: sections,
      filtered: filterSections(sections, selectedDateStr),
      marked: getMarked(sections, selectedDateStr, primaryColor),
    });
  };

  const filterSections = (sections, selectedDate) => {
    const filteredList = sections.filter((x) => x.title === selectedDate);
    if (isEmpty(filteredList)) {
      return [{ title: selectedDate, data: [{}] }];
    }
    return filteredList;
  };

  useEffect(() => {
    moment.tz.setDefault(localSettings.timezone);
    getAvailableTimes();
  }, [localSettings, dates.startOfMonth]);

  const getSections = (timesList, userObj, eventObj) => {
    return timesList.map((time) => {
      const timeMoment = moment(time.day);
      var data = time.meetingTimes
        .filter((x) => x.status === "available")
        .map((meeting) => {
          const meet = moment(meeting.start);
          return {
            ...eventObj,
            username: username,
            name: userObj.name,
            picture: userObj.picture,
            title: "Available Booking",
            meetingLength: meeting.meetingLength,
            hour: meet.format("h:mma"),
            available: true,
            start: meeting.start,
          };
        });
      if (!data || !data.length) {
        data = [];
      }
      return {
        title: timeMoment.format("YYYY-MM-DD"),
        data: data,
      };
    });
  };

  const onMonthChange = (months) => {
    if (isEmpty(months)) {
      return;
    }
    console.log("Month change", months[0].dateString, times);
    const monthDate = moment(months[0].dateString).startOf("month");
    setDates({
      ...dates,
      todayDate: moment(monthDate),
      selectedMonth: moment(monthDate),
      startDate: monthDate.isAfter(moment()) ? moment(monthDate) : moment(),
      startOfMonth: moment(monthDate).startOf("month"),
      endOfMonth: moment(monthDate).endOf("month"),
      monthRange: moment.range(
        moment(monthDate).startOf("month"),
        moment(monthDate).endOf("month")
      ),
      maxStartDate: moment().add(event.meetingFutureBooking, "days"),
    });
  };

  const onDateChanged = (dateStr) => {
    console.log("Date changed", dateStr.dateString, times, dates);
    const monthDate = moment(dateStr.dateString).startOf("month");
    const newDates = {
      ...dates,
      selectedDate: dateStr.dateString,
      todayDate: moment(monthDate),
      selectedMonth: moment(monthDate),
      startDate: monthDate.isAfter(moment()) ? moment(monthDate) : moment(),
      startOfMonth: moment(monthDate).startOf("month"),
      endOfMonth: moment(monthDate).endOf("month"),
      monthRange: moment.range(
        moment(monthDate).startOf("month"),
        moment(monthDate).endOf("month")
      ),
    };
    setDates(newDates);
    setTimesData(times.original, newDates.selectedDate, primaryColor);
  };

  const formatUser = (userObj, settings) => {
    return {
      ...user,
      initials: userObj.name
        .split(" ")
        .map((n) => n[0])
        .join(""),
      ...userObj,
      ...settings,
    };
  };

  const getFirstDate = (sections) => {
    if (isEmpty(sections)) {
      return moment().format("YYYY-MM-DD");
    }
    const firstDay = sections.find((x) => {
      var found = false;
      x.data.forEach((x) => {
        if (x.available) {
          found = true;
        }
      });
      return found;
    });
    return firstDay ? firstDay.title : moment().format("YYYY-MM-DD");
  };

  const getMarked = (sections, selectedDateStr, primaryColor) => {
    const availableDays = {};
    const marked = {};
    for (const day of sections) {
      if (!isEmpty(day.data)) {
        availableDays[day.title] = true;
      }
    }
    for (const day of dates.monthRange.by("day")) {
      const title = day.format("YYYY-MM-DD");
      if (availableDays[title] && title === selectedDateStr) {
        marked[title] = {
          selected: true,
          selectedColor: primaryColor,
          marked: true,
        };
      } else if (availableDays[title]) {
        marked[title] = { marked: true, dotColor: primaryColor };
      } else if (title === selectedDateStr) {
        marked[title] = {
          selected: true,
          selectedColor: primaryColor,
          disabled: true,
          disableTouchEvent: true,
        };
      } else {
        marked[title] = {
          disabled: true,
          selectedColor: primaryColor,
          disableTouchEvent: true,
        };
      }
    }
    return marked;
  };

  const renderArrow = (direction) => {
    if (direction === "left") {
      return <ChevronLeftIcon />;
    } else {
      return <ChevronRightIcon />;
    }
  };

  if (loadingScreen) {
    return <LoadingScreen open={loadingScreen} />;
  }

  const calendarTheme = {
    backgroundColor: "transparent",
    calendarBackground: "transparent",
    textSectionTitleColor: theme.palette.text.primary,
    textSectionTitleDisabledColor: theme.palette.text.primary,
    selectedDayTextColor: theme.palette.text.primary,
    todayTextColor: theme.palette.text.primary,
    dayTextColor: theme.palette.text.primary,
    textDisabledColor: theme.palette.text.disabled,
    monthTextColor: theme.palette.text.primary,
    indicatorColor: theme.palette.primary.main,
  };

  return (
    <Box>
      <Container>
        <Paper elevation={2} style={styles.root}>
          <Container style={styles.textGrid}>
            <Grid container>
              <Grid xs={11} item>
                <Typography
                  gutterBottom
                  variant="h6"
                  color="textSecondary"
                  style={{
                    flexDirection: "row",
                    alignItems: "center",
                    display: "flex",
                  }}
                >
                  <Avatar
                    style={{ marginRight: 8 }}
                    alt={user.name}
                    src={user.picture}
                  />{" "}
                  {settings.eventPageTitle}
                </Typography>
                <Typography gutterBottom variant="h6" component="h6">
                  {event.title}
                </Typography>
                <Typography gutterBottom variant="body2">
                  {event.description}
                </Typography>
              </Grid>
              <Grid xs={1} item>
                <div style={{ float: "right" }}>
                  <IconButton
                    onClick={() =>
                      setThemeMode(mode === "light" ? "dark" : "light")
                    }
                    color="inherit"
                  >
                    {theme.palette.mode === "dark" ? (
                      <Brightness7Icon />
                    ) : (
                      <Brightness4Icon />
                    )}
                  </IconButton>
                </div>
              </Grid>
            </Grid>
          </Container>
          {theme.palette.mode === "dark" && (
            <Calendar
              displayLoadingIndicator={spinner}
              theme={calendarTheme}
              markedDates={times.marked}
              renderArrow={renderArrow}
              onDayPress={onDateChanged}
              onVisibleMonthsChange={onMonthChange}
            />
          )}
          {theme.palette.mode === "light" && (
            <Calendar
              displayLoadingIndicator={spinner}
              theme={calendarTheme}
              markedDates={times.marked}
              renderArrow={renderArrow}
              onDayPress={onDateChanged}
              onVisibleMonthsChange={onMonthChange}
            />
          )}
          <AgendaList
            sections={times.filtered}
            timezone={localSettings.timezone}
            setTimezone={(timezone) => {
              setLocalSettings({
                ...localSettings,
                timezone: timezone,
              });
            }}
          />
        </Paper>
      </Container>
    </Box>
  );
};

export default ScheduleScreen;

const styles = StyleSheet.create({
  section: {
    color: "grey",
    textTransform: "capitalize",
  },
  nameGrid: {
    display: "flex",
    alignItems: "center",
  },
  root: {
    marginTop: 20,
    marginBottom: 20,
  },
  textGrid: {
    paddingTop: 15,
  },
});
